import React, { useMemo, useEffect, useState, useCallback } from 'react';
import { getConnectHost, withConnectHost } from '@atlassian/jira-connect-utils';
import { EcosystemModuleAsync } from '@atlassian/jira-connect-utils-async';
import {
	type AnalyticsAttributes,
	useAnalyticsEvents,
	ContextualAnalyticsData,
	fireOperationalAnalytics,
	fireScreenAnalytics,
	FireOperationalAnalytics,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import {
	ExperiencePerformanceTypes,
	ExperienceTypes,
	UFOExperience,
	useUFOComponentExperience,
} from '@atlassian/ufo';
import type { ConnectBackgroundScript as ConnectBackgroundScriptType } from '../../types';

type Props = {
	id: string;
	packageName: string;
	item: ConnectBackgroundScriptType;
	analytics?: AnalyticsAttributes;
};

export const dashboardBackgroundScriptLoadTime = new UFOExperience(
	'connect-background-scripts.load-time',
	{
		type: ExperienceTypes.Load,
		performanceType: ExperiencePerformanceTypes.Custom,
	},
);

export const ConnectBackgroundScriptModule = ({ item, packageName, id, analytics }: Props) => {
	const [connectHost, setConnectHost] = useState(() => getConnectHost());
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { appKey, moduleKey, options } = item;

	const analyticsAttributes = useMemo(
		() => ({
			appKey,
			moduleKey,
			...analytics,
		}),
		[analytics, appKey, moduleKey],
	);

	useUFOComponentExperience(dashboardBackgroundScriptLoadTime);

	const onLoadingComplete = useCallback(() => {
		dashboardBackgroundScriptLoadTime.success();
		fireScreenAnalytics(createAnalyticsEvent({}), analyticsAttributes);
	}, [analyticsAttributes, createAnalyticsEvent]);

	const onTimeout = useCallback(() => {
		dashboardBackgroundScriptLoadTime.abort();
		fireOperationalAnalytics(
			createAnalyticsEvent({}),
			`${packageName}.${id} timeout`,
			analyticsAttributes,
		);
	}, [analyticsAttributes, createAnalyticsEvent, packageName, id]);

	useEffect(() => {
		connectHost ||
			withConnectHost((host) => setConnectHost(host)).catch(() => {
				dashboardBackgroundScriptLoadTime.failure();

				fireOperationalAnalytics(
					createAnalyticsEvent({}),
					`${packageName}.${id} failed`,
					analyticsAttributes,
				);
			});
	}, [connectHost, analyticsAttributes, createAnalyticsEvent, packageName, id]);

	if (!connectHost) {
		return null;
	}

	return (
		<EcosystemModuleAsync
			appKey={appKey}
			moduleKey={moduleKey}
			options={options}
			appName={`${appKey}-${moduleKey}`}
			location="dashboardBackgroundScript"
			onLoadingComplete={onLoadingComplete}
			onTimeout={onTimeout}
		/>
	);
};

export const ConnectBackgroundScript = ({ id, packageName, item, analytics, ...rest }: Props) => {
	const { appKey, moduleKey } = item;

	return (
		<ContextualAnalyticsData
			key={`connect-iFrame-${appKey}-${moduleKey}`}
			sourceName={`${packageName}.${id}`}
			sourceType={SCREEN}
			{...rest}
		>
			<FireOperationalAnalytics
				action="initialized"
				actionSubject={`${packageName}.${id}`}
				actionSubjectId={`connect-iFrame-${appKey}-${moduleKey}`}
				attributes={{
					appKey,
					...analytics,
				}}
			/>
			<ConnectBackgroundScriptModule
				item={item}
				id={id}
				packageName={packageName}
				analytics={analytics}
			/>
		</ContextualAnalyticsData>
	);
};
